import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography
} from '@mui/material';
import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import * as yup from 'yup';
import type { GanadoEnCorral } from '../../api-client/ganado/ganado';
import AppDialog from '../../components/ui/AppDialog';
import ComboBox from '../../components/ui/Combobox';
import { useGanadoStore } from '../../stores/ganado/ganado.store';
import { useUIStore } from '../../stores/ui/ui.store';
import { useZonasStore } from '../../stores/zonas/zonas.store';
import transformToOptions from '../../utils/transform-to-options';
import EncabezadoDialogo from './EncabezadoDialogo';

interface CambioZonaDialogoProps {
  open: boolean;
  onClose: () => void;
  animal: GanadoEnCorral;
}

const CambioZonaDialogo = ({ open, onClose, animal }: CambioZonaDialogoProps) => {
  const { loading } = useUIStore();
  const { cambiarZona } = useGanadoStore();
  const { actives: zonas, cargarActivas: cargarZonasActivas } = useZonasStore();
  const [showConfirm, setShowConfirm] = useState(false);
  const form = useFormik({
    initialValues: {
      idZona: animal.idZona
    },
    validationSchema: yup.object({
      idZona: yup.number().required('La zona es requerida').min(1, 'La zona es requerida')
    }),
    onSubmit: async (values) => {
      await cambiarZona({
        folioInterno: animal.folioInterno,
        partida: animal.partida,
        idZona: values.idZona
      });

      onClose();
    }
  });

  useEffect(() => {
    cargarZonasActivas();
  }, []);

  const confirm = () => {
    form.submitForm();
    if (!form.isValid) return;
    setShowConfirm(false);
  };

  const zonasOptions = transformToOptions(zonas);

  return (
    <>
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>CAMBIAR ZONA</DialogTitle>
        <DialogContent>
          <form noValidate autoComplete="off" onSubmit={form.submitForm}>
            <Grid container direction="row" spacing={2} sx={{ width: '100%' }}>
              <EncabezadoDialogo animal={animal} />
              <Grid size={6} alignContent="center">
                <Typography>Asignar a la zona:</Typography>
              </Grid>
              <Grid size={6}>
                <ComboBox
                  name="idZona"
                  label=""
                  options={zonasOptions}
                  value={form.values.idZona}
                  disabled={loading}
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  error={form.touched.idZona && Boolean(form.errors.idZona)}
                  helperText={form.touched.idZona ? form.errors.idZona : undefined}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            onClick={() => setShowConfirm(true)}
            disabled={loading}
            startIcon={<CheckCircleIcon />}
            disableElevation
          >
            Guardar
          </Button>
          <Button
            variant="contained"
            startIcon={<ExitToAppIcon />}
            onClick={onClose}
            disabled={loading}
            disableElevation
          >
            Salir
          </Button>
        </DialogActions>
      </Dialog>
      <AppDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        title="Confirme"
        message="¿Está seguro de guardar y registrar el movimiento?"
        buttons={
          <>
            <Button color="secondary" variant="contained" disableElevation onClick={confirm}>
              Sí
            </Button>
            <Button variant="contained" disableElevation onClick={() => setShowConfirm(false)}>
              No
            </Button>
          </>
        }
      />
    </>
  );
};

export default CambioZonaDialogo;
